import { useGo } from "@refinedev/core";
import type { BaseKey } from "@refinedev/core";

import { cn } from "@/lib/utils";

/**
 * "Enrichment runs in the background" — the one curator-facing consequence of
 * enrichment becoming asynchronous, generalised past geodata.
 *
 * **What changed.** Enrichment used to be synchronous on create: geodata's
 * handler awaited an in-process `EnrichGeodataCommand`, so `POST /api/geodata`
 * did not return until the enrichment had been computed *and written*, and IMS
 * landed on a row that already had `about` and `aliases`. Now the create returns
 * as soon as the Cloud Task is enqueued and the post-create view renders an
 * **unenriched** row. Nothing is broken — the enrichment lands
 * seconds-to-minutes later — but a curator cannot tell "queued" from "didn't
 * happen", and the natural reaction is to press the enrich button again.
 *
 * **Why this is not geodata-only.** The hint was first scoped to geodata,
 * because geodata was the sole *synchronous* create at the time. `Unavailable`
 * is now the resting state of every Tina-created entity until its tail runs, so
 * the same "Enrich" button on `/events`, `/venues` and `/artists` teaches the
 * same wrong model. The hint and the suppression go to all four.
 *
 * **Do not gate this on `Pending`.** The obvious gate — and the one the original
 * design asked for — is `enrichmentStatus === "Pending"`, a value **nothing
 * writes**. The backend enum is
 * `Unavailable | InProgress | Enriched | NeedsReview | Failed`
 * (`EnrichmentStatusDb`), and the phantom `Pending` is gone from the shared
 * vocabulary. Gated that way, the hint would never render. The correct gate is
 * `InProgress`, plus the `justCreated` signal for the `Unavailable`/null case.
 * Both live in {@link isEnrichmentInFlight}.
 */

/** Query parameter the create redirect carries. See {@link useJustCreated}. */
export const JUST_CREATED_PARAM = "created";

export const ENRICHMENT_ASYNC_HINT =
  "Enrichment runs in the background — refresh in a moment.";

/**
 * True while an enrichment is (or is about to be) running for this entity.
 *
 * Two arms, and both are needed:
 *
 * - **`InProgress`** — a job is actually executing. This is the arm the phantom
 *   `Pending` was reaching for.
 * - **just created with no status yet** — the create returned before the job
 *   row existed, so the entity reads `Unavailable`/null for the first seconds
 *   of its life. Without this arm the most misleading window has no hint.
 *
 * `Failed`, `Enriched` and `NeedsReview` are deliberately excluded even when
 * `justCreated` is set: those are answers, and an entity that answered is not
 * waiting on anything.
 */
export function isEnrichmentInFlight(
  status: string | null | undefined,
  justCreated = false,
): boolean {
  if (status === "InProgress") return true;
  return justCreated && (!status || status === "Unavailable");
}

/**
 * Whether to hide the "Enrich" / "Run enrichment" call-to-action.
 *
 * Identical to {@link isEnrichmentInFlight} by construction, and that is the
 * point: the button is offered exactly when pressing it would *do* something.
 * Pressing it during the in-flight window is harmless — the backend's
 * 10-minute named-task debounce and Tina's `ev_enrichment_active_uq` make it an
 * `already_queued` no-op — but it teaches the curator that enrichment is
 * something they must trigger, which after the migration it is not.
 */
export function shouldSuppressEnrichCta(
  status: string | null | undefined,
  justCreated = false,
): boolean {
  return isEnrichmentInFlight(status, justCreated);
}

/**
 * Whether to hide the entity previews' enrichment button — the artist / venue /
 * event aside's `Review · View · Enrich`, which **navigates** to
 * `/{entity}/enrichment/:id` and queues nothing.
 *
 * Narrower than {@link shouldSuppressEnrichCta}, and the gap is the bug. That
 * predicate answers "hide the button that POSTs", which is right for geodata's
 * `Run enrichment` — it really does queue a job. The three previews reused it
 * for a link, so an `InProgress` took away the only route from the aside into
 * the review page. `InProgress` is written *before* the work rather than after
 * it, so a worker that dies mid-job leaves the entity sitting on it: "refresh
 * in a moment" forever, no button, no way to read the enrichment the entity
 * already had, nothing at all to press.
 *
 * **No staleness escape, deliberately.** Tina bounds a stalled `InProgress`
 * itself (`TINA_ENRICH_INPROGRESS_STALE_S`, 2 h by default). IMS can see
 * neither the bound — no route exposes it — nor the moment the status flipped:
 * the entity carries no timestamp for it, and its `updatedAt` moves on any
 * edit, curator edits included. An escape built here would therefore be a
 * second, *wrong* copy of a number Tina owns, and would fire early on any
 * entity someone touched. Leaving the link open needs neither, and is right at
 * every moment of the window rather than only after two hours.
 *
 * The just-created window still suppresses: it is seconds long, it heals the
 * instant any status is written, and it exists precisely so a brand-new row does
 * not offer an "Enrich" that teaches the wrong model.
 */
export function shouldSuppressEnrichmentLink(
  status: string | null | undefined,
  justCreated = false,
): boolean {
  return isEnrichmentInFlight(status, justCreated) && status !== "InProgress";
}

/** The hint line itself. Renders nothing unless an enrichment is in flight. */
export function EnrichmentAsyncHint({
  status,
  justCreated,
  className,
}: {
  status: string | null | undefined;
  justCreated?: boolean;
  className?: string;
}) {
  if (!isEnrichmentInFlight(status, justCreated)) return null;
  return (
    <p
      className={cn("text-xs leading-relaxed text-muted-foreground", className)}
    >
      {ENRICHMENT_ASYNC_HINT}
    </p>
  );
}

/**
 * Reads the one-shot `?created=1` signal off the edit URL.
 *
 * It does not need clearing: the hint's other arm requires the status to still
 * be `Unavailable`/null, so the moment the job writes a real status the hint
 * stops rendering even though the parameter is still in the address bar.
 *
 * Reads `window.location` rather than `useSearchParams` deliberately. The
 * signal is set by a route change (create → edit), so the component that reads
 * it always mounts fresh and a subscription buys nothing; and taking a Router
 * context dependency would make every edit page unrenderable in a unit test
 * that does not wrap it in a `MemoryRouter` — a cost paid by tests that have
 * nothing to do with enrichment.
 */
export function useJustCreated(): boolean {
  if (typeof window === "undefined") return false;
  return (
    new URLSearchParams(window.location.search).get(JUST_CREATED_PARAM) === "1"
  );
}

/**
 * Navigate to an entity's edit page carrying the just-created signal — the
 * replacement for `useNavigation().edit(resource, id, …)` in a create flow.
 *
 * `edit()` has no way to attach a query parameter, which is the only reason
 * this reaches for `useGo`.
 */
export function useEditCreated(): (
  resource: string,
  id: BaseKey,
  type?: "push" | "replace",
) => void {
  const go = useGo();
  return (resource, id, type = "replace") => {
    go({
      to: { resource, id, action: "edit" },
      query: { [JUST_CREATED_PARAM]: "1" },
      type,
    });
  };
}
